import React, { useMemo, useState, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import axios from "../../../api/axios";
import RegistrationForm from "../forms/RegistrationForm";
import { EventTabs } from "../../../utils/constants";
import { formatMoney } from "../../../utils/helpers";
import { DataGrid, GridToolbar, GridActionsCellItem } from '@mui/x-data-grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useSnackbar } from 'notistack';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tabs,
  Tab,
  Switch
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  ContentCopy as CloneIcon
} from "@mui/icons-material";
import styles from "./Registration.module.css"

const Registration = (props) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [eventData, setEventData] = useState(null);
  let { eventid } = useParams();

const urlSegment =
  location.pathname.split("/")[3]?.toLowerCase() || "general";

const activeTabName =
  (
    EventTabs.find(
      t => t.path.toLowerCase() === urlSegment
    ) ||
    EventTabs[0]
  ).name;

  const handleTabChange = (_, newValue) => {
  const tab = EventTabs.find(t => t.name === newValue);

  if (!tab || !eventid) {
    return;
  }

  navigate(`/admin/events/${tab.path}/${eventid}`);
};

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        getActions: (params) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={() => navigate(`/admin/events/registration/${eventid}/${params.row.RegistrationID}`)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => openDelete(params.row)}
            disabled={isDeleting}
          />,
          <GridActionsCellItem
            key="clone"
            icon={<CloneIcon/>}
            label="Clone"
            onClick={() => handleDuplicate(params.row)}
            disabled={isDuplicating}
            />
        ]
      },
      { field: "RegistrationName", headerName: "Name", minWidth: 260, flex: 1.2 },
      { field: "RegistrationID", headerName: "ID", minWidth: 90, flex: 0.4 },
      {
        field: "RegistrationCost",
        headerName: "Cost",
        minWidth: 160,
        flex: 0.7,
        valueFormatter: (params) => {
          if (params.value == null) return '$0.00';
          return formatMoney(params.value);
        }
      },
      {
        field: "RegistrationDescription",
        headerName: "Description",
        minWidth: 160,
        flex: 0.7,
      },
      {
        field: "RegistrationCapacity",
        headerName: "Capacity",
        minWidth: 220,
        flex: 1,
      },
      {
        field: "RegistrationType",
        headerName: "Type",
        minWidth: 150,
        flex: 0.6,
        valueGetter: (p) => typeLabel(p.row)
      }
    ],
    [isDeleting, eventid]
  );

  const typeLabel = (row) => parseInt(row?.RegistrationType) === 0 ? "Profile" : "Form";
  const [payLater, setPayLater] = useState(false);

  useEffect(() => {
    if (eventData?.PayLater !== undefined) {
        setPayLater(Boolean(eventData.PayLater));
    }
}, [eventData]);


  const fetchData = async () => {
    setIsLoading(true);
    try {
      const result = await axios.get(`/api/registrations/events/${eventid}`);
      if (Array.isArray(result.data)) {
        setData(result.data);
      } else {
        setData([result.data]);
      }
      enqueueSnackbar('Registrations fetch success');
    } catch (error) {
      setError(true);
      enqueueSnackbar('Event fetch failed');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Fetch event details for header
  useEffect(() => {
    if (!eventid) return;
    let cancelled = false;
    const run = async () => {
      try {
        const resp = await axios.get(`/api/events/${eventid}`);
        if (!cancelled) setEventData(resp.data);
        //console.log('Event Data: ',resp.data);
      } catch (e) {
        // Non-critical — header shows fallback text
      }
    };
    run();
    return () => { cancelled = true; };
  }, [eventid]);

  const handleDuplicate = async (row) => {
    const id = row?.RegistrationID;
    if (!id) return;
    setIsDuplicating(true);
    try {
      const resp = await axios.post(`/api/registrations/clone/${id}`);
      const newId = resp.data?.RegistrationID;
      enqueueSnackbar("registration duplicated", { variant: "success" });
      //if (newId) navigate(`/admin/registration/${newId}`);
      await fetchData();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || "Failed to duplicate event", { variant: "error" });
    } finally {
      setIsDuplicating(false);
    }
  };

  const openDelete = (row) => setDeleteDialog({ open: true, registration: row });
  const closeDelete = () => {
    if (isDeleting) return;
    setDeleteDialog({ open: false, event: null });
  };

  const confirmDelete = async () => {
    const id = deleteDialog?.registration?.RegistrationID;
    if (!id) return;
    setIsDeleting(true);
    try {
      await axios.delete(`/api/registrations/${id}`);
      enqueueSnackbar("Registration deleted", { variant: "success" });
      setDeleteDialog({ open: false, event: null });
      await fetchData();
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.error || "Failed to delete registration", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  

  const headerTitle = eventData?.BP_Title
    ? `Edit Registration: ${eventData.BP_Title}`
    : 'Edit Registration';

  const stickyTabBar = (
    <Box
      sx={{
        position: 'sticky',
        top: 40,
        zIndex: 100,
        backgroundColor: 'white',
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}
    >
      <Box sx={{ px: 3 }}>
        <Tabs
  value={activeTabName}
  onChange={handleTabChange}
  variant="scrollable"
  scrollButtons="auto"
  allowScrollButtonsMobile
  TabIndicatorProps={{
    sx: {
      height: 3,
      borderRadius: 2,
      backgroundColor: "#0074c8"
    }
  }}
  sx={{
    minHeight: 44,
    "& .MuiTabs-flexContainer": {
      gap: 1
    },
    "& .MuiTab-root": {
      textTransform: "none",
      fontFamily: "proxima-nova, sans-serif",
      fontWeight: 600,
      fontSize: { xs: 14, sm: 16 },
      minHeight: 44,
      px: 2,
      borderRadius: 1,
      whiteSpace: "nowrap",
      color: "text.primary",

      "&:hover": {
        backgroundColor: "rgba(0,0,0,0.06)"
      },

      "&.Mui-selected": {
        color: "#0074c8"
      }
    }
  }}
>
  {EventTabs.map((t) => (
    <Tab
      key={t.id}
      value={t.name}
      label={t.label || t.name}
      wrapped={false}
    />
  ))}
</Tabs>
      </Box>
    </Box>
  );

  if (isLoading) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">{headerTitle}</h1>
                <p>Manage registration details</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          {stickyTabBar}
          <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography>Loading registrations...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">{headerTitle}</h1>
                <p>Manage registration details</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          {stickyTabBar}
          <Box sx={{ p: 3 }}>
            <Typography color="error">Error occurred while fetching registrations. Please contact an admin.</Typography>
          </Box>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">{headerTitle}</h1>
              <p>Manage registration details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
        {/* Sticky Tab Bar */}
        {stickyTabBar}

        <Box sx={{ p: 3 }}>
          <Paper className={styles.searchPaper}>
            <Box className={styles.buttonContainer} sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'stretch', md: 'center' }, justifyContent: 'space-between', gap: 2, width: '100%' }}>
    <Button
        variant="contained"
        onClick={() => navigate(`/admin/events/registration/add/${eventid}`)}
        startIcon={<AddIcon />}
        className={styles.addButton}
    >
        Add New Registration
    </Button>

    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, px: 2, py: 1, borderRadius: 2, border: '1px solid', borderColor: payLater ? 'primary.light' : 'divider', backgroundColor: payLater ? 'rgba(0,116,200,0.05)' : 'grey.50', transition: 'all 0.2s ease' }}>
        <Typography variant="body2" sx={{ fontFamily: 'proxima-nova, sans-serif', fontWeight: 600, color: payLater ? 'primary.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
           Allow Pay Later
        </Typography>
        <Switch
            checked={payLater}
            onChange={(e) => {
                setPayLater(e.target.checked);
                axios.patch(`/api/events/${eventid}`, { PayLater: e.target.checked }).then(resp => {
                  //console.log(resp);
                }).catch(ex => {
                })
            }}
            size="small"
            sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#0074c8' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0074c8' },
            }}
        />
        <small>If disabled, event will not allow for the pay later option</small>
    </Box>
</Box>
          </Paper>

          <Paper className={styles.dataGridPaper}>
            <DataGrid
              columns={columns}
              rows={data}
              getRowId={(row) => row.RegistrationID}
              onRowClick={(row, event) => {
                if (!event.ignore) {
                  navigate("/admin/events/registration/" + eventid + '/' + row.row.RegistrationID);
                }
              }}
              disableMultipleSelection
              disableSelectionOnClick
              loading={isLoading}
              autoHeight
              disableColumnFilter
              disableDensitySelector
              hideFooterPagination
              slots={{ toolbar: null }}
              components={{
                NoRowsOverlay: () => (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                    <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No registrations yet</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>Click "Add New Registration" to create one.</Typography>
                  </Box>
                ),
              }}
              className="eventsDataGrid"
            />
          </Paper>
        </Box>
      </div>

      <Dialog open={deleteDialog.open} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle className={styles.dialogTitle}>Delete Registration</DialogTitle>
        <DialogContent>
          <Typography variant="body1" className={styles.dialogText}>
            Are you sure you want to permanently delete this registration?
          </Typography>
          <Box className={styles.userInfoBox}>
            <Typography variant="body2" className={styles.userInfoName}>
              <strong>Title:</strong> {deleteDialog?.registration?.RegistrationName || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>ID:</strong> {deleteDialog?.registration?.RegistrationID || "N/A"}
            </Typography>
          </Box>
          <Alert severity="error">
            <strong>Warning:</strong> This action cannot be undone. The registration record will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeDelete} variant="outlined" className={styles.cancelButton} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            className={styles.confirmButtonDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress color="inherit" size={16} /> : <DeleteIcon />}
          >
            Delete Registration
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}

export default Registration;
